import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plug, CheckCircle2, XCircle, AlertTriangle, Store, Loader2, Link2, ArrowRight } from "lucide-react";

const MP_STATUS = {
  connected: { label: "Connected", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  not_connected: { label: "Not Connected", cls: "bg-slate-100 text-slate-600", Icon: XCircle },
  setup_required: { label: "Setup Required", cls: "bg-sky-100 text-sky-700", Icon: AlertTriangle },
  secrets_missing: { label: "Secrets Missing", cls: "bg-rose-100 text-rose-700", Icon: AlertTriangle },
  error: { label: "Token Expired", cls: "bg-rose-100 text-rose-700", Icon: AlertTriangle },
};

function MpStatusPill({ status }) {
  const m = MP_STATUS[status] || MP_STATUS.not_connected;
  const { Icon } = m;
  return (
    <span data-testid={`mp-entry-status-${status}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${m.cls}`}>
      <Icon className="w-3.5 h-3.5" />{m.label}
    </span>
  );
}

// Canonical marketplace entrypoint. Reuses the ERP /marketplace/connections readiness and routes to the
// single canonical Connect flow at /marketplace-connections. No second connector / no duplicate token store.
function MarketplaceConnectPanel() {
  const navigate = useNavigate();
  const [platforms, setPlatforms] = useState(null);

  useEffect(() => {
    // Silent: users with integration:view but not marketplace:manage may be denied the readiness read.
    api.get("/marketplace/connections")
      .then((r) => setPlatforms(r.data?.platforms || []))
      .catch(() => setPlatforms([]));
  }, []);

  const byId = Object.fromEntries((platforms || []).map((p) => [p.platform, p]));
  const rows = [["tiktok", "TikTok Shop"], ["shopee", "Shopee"]];

  return (
    <div data-testid="integration-card-marketplace" className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
        <div className="w-10 h-10 rounded-lg bg-slate-900 grid place-items-center text-white">
          <Store className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="font-heading font-bold text-slate-900">Marketplace Connections</div>
          <div className="text-xs text-slate-500">FASFONE ERP · secure one-click authorization</div>
        </div>
      </div>

      <div className="px-5 py-4">
        <div className="space-y-2" data-testid="mp-entry-rows">
          {rows.map(([id, name]) => {
            const p = byId[id];
            return (
              <div key={id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3"
                data-testid={`mp-entry-${id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Plug className="w-4 h-4 text-sky-600 flex-shrink-0" />
                  <span className="text-sm font-medium truncate">{name}</span>
                </div>
                {platforms === null
                  ? <Loader2 className="w-4 h-4 animate-spin text-slate-300" data-testid={`mp-entry-loading-${id}`} />
                  : p
                    ? <MpStatusPill status={p.status} />
                    : <span className="text-xs text-slate-400" data-testid={`mp-entry-managed-${id}`}>Managed in Marketplace Connections</span>}
              </div>
            );
          })}
        </div>

        <Button size="sm" onClick={() => navigate("/marketplace-connections")}
          data-testid="open-marketplace-connections-btn" className="mt-4 w-full bg-slate-900 hover:bg-slate-800">
          <Link2 className="w-4 h-4 mr-1.5" />Open Marketplace Connections<ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>

        <p className="mt-3 text-xs text-slate-500">
          TikTok Shop and Shopee are authorized through secure server-side OAuth in the ERP Marketplace Connections
          workspace. Credentials and tokens are stored on the server and never shown here.
        </p>
      </div>
    </div>
  );
}

function ComingSoonCard({ name, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-dashed border-slate-200 bg-white/50 overflow-hidden opacity-80">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
        <div className="w-10 h-10 rounded-lg bg-slate-200 grid place-items-center text-slate-400">
          <Store className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="font-heading font-bold text-slate-700">{name}</div>
          <div className="text-xs text-slate-400">Marketplace integration</div>
        </div>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-400">
          <Plug className="w-3.5 h-3.5" />Coming Soon
        </span>
      </div>
      <div className="px-5 py-4">
        <p className="text-sm text-slate-400">Integration coming soon.</p>
      </div>
    </div>
  );
}

export default function Integrations() {
  return (
    <div data-testid="integrations-page">
      <header className="mb-6">
        <div className="caption">Connections</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">Integrations</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Connect FASFONE ERP to your marketplaces and services. Credentials and tokens are stored securely on the server and never shown here.
        </p>
      </header>

      <section className="mb-8">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Marketplace Integrations</h2>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <MarketplaceConnectPanel />
          <ComingSoonCard name="Lazada" testid="integration-card-lazada" />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Services (Coming Soon)</h2>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <ComingSoonCard name="SiteGiant" testid="integration-card-sitegiant" />
          <ComingSoonCard name="PayRecon" testid="integration-card-payrecon" />
          <ComingSoonCard name="Bukku" testid="integration-card-bukku" />
        </div>
      </section>
    </div>
  );
}
